"use client";

import { useQuery } from "@tanstack/react-query";

import React, { useMemo, useRef, useState } from "react";
import {
  RiCalendarLine,
  RiCheckLine,
  RiCircleLine,
  RiCloseCircleLine,
  RiExpandUpDownLine,
  RiFlagLine,
  RiMapPinLine,
  RiPlayCircleLine,
  RiSearchLine,
  RiUser3Line,
  RiUserHeartLine,
} from "react-icons/ri";

import PatientDisplay from "@/components/patient/patient-display";
import ProviderAvatar from "@/components/provider-avatar";
import Button from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useLocations } from "@/hooks/use-locations";
import { useProviders } from "@/hooks/use-providers";

import { pickedDay } from "@/lib/clinic-clock";
import { formatDate, formatName, formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import { UserType } from "@/types/enums";
import { PatientSummary } from "@/types/patient";
import { TaskPriority, TaskStatus } from "@/types/task";
import type { Team } from "@/types/team";

import { useTeams } from "@/api/provider/teams/teams-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";

// --- Configs for labels and icons ---
export const STATUS_CONFIG: Record<
  TaskStatus,
  {
    label: string;
    icon: React.ElementType<{ className?: string }>;
    color: string;
  }
> = {
  [TaskStatus.OPEN]: {
    label: "Open",
    icon: RiCircleLine,
    color: "text-foreground-muted",
  },
  [TaskStatus.IN_PROGRESS]: {
    label: "In Progress",
    icon: RiPlayCircleLine,
    color: "text-accent-foreground",
  },
  [TaskStatus.COMPLETED]: {
    label: "Completed",
    icon: RiCheckLine,
    color: "text-status-success-text",
  },
  [TaskStatus.CANCELLED]: {
    label: "Cancelled",
    icon: RiCloseCircleLine,
    color: "text-status-error-text",
  },
};

export const PRIORITY_CONFIG: Record<
  TaskPriority,
  { label: string; color: string }
> = {
  [TaskPriority.URGENT]: {
    label: "Urgent",
    color: "text-status-error-text",
  },
  [TaskPriority.HIGH]: {
    label: "High",
    color: "text-status-warning-text",
  },
  [TaskPriority.NORMAL]: {
    label: "Normal",
    color: "text-foreground-muted",
  },
  [TaskPriority.LOW]: {
    label: "Low",
    color: "text-accent-foreground",
  },
};

export const subtleTriggerClasses =
  "h-7 w-fit gap-2 border-none px-2 py-0 text-caption shadow-none bg-transparent hover:bg-hover font-normal";

// --- Status Select ---
export function StatusBadge({
  value,
  onChange,
  className,
  isEditMode,
}: {
  value: TaskStatus;
  onChange: (v: TaskStatus) => void;
  className?: string;
  isEditMode?: boolean;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskStatus)}>
      <SelectTrigger className={cx(subtleTriggerClasses, className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(TaskStatus).map((status) => {
          const optConfig = STATUS_CONFIG[status];
          const OptIcon = optConfig.icon;
          return (
            <SelectItem key={status} value={status}>
              <span className="flex items-center gap-2">
                <OptIcon className={cx("size-4", optConfig.color)} />
                {optConfig.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

// --- Priority Select ---
export function PriorityBadge({
  value,
  onChange,
  className,
}: {
  value: TaskPriority;
  onChange: (v: TaskPriority) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskPriority)}>
      <SelectTrigger className={cx(subtleTriggerClasses, className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(TaskPriority).map((priority) => {
          const optConfig = PRIORITY_CONFIG[priority];
          return (
            <SelectItem key={priority} value={priority}>
              <span className="flex items-center gap-2">
                <RiFlagLine className={cx("size-4", optConfig.color)} />
                {optConfig.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

// --- Assignee Search Select ---
export function AssigneeBadge({
  value,
  onChange,
  className,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const { providers } = useProviders();

  // Exclude internal Decoda staff from assignment, matching other provider
  // pickers (e.g. tip recipient). Fall back to all providers if filtering would
  // leave none, so the dropdown is never empty on Decoda-only tenants.
  const assignableProviders = useMemo(() => {
    const clinic = providers.filter(
      (p) => !p.email?.endsWith("@decodahealth.com"),
    );
    return clinic.length > 0 ? clinic : providers;
  }, [providers]);

  const filtered = assignableProviders.filter((p) =>
    formatStaffName(p).toLowerCase().includes(search.toLowerCase()),
  );

  const selectedProvider = providers.find((p) => p.id === value);
  const displayLabel = selectedProvider
    ? formatStaffName(selectedProvider)
    : "Unassigned";

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cx(
            subtleTriggerClasses,
            "!justify-between group",
            className,
          )}
        >
          <span className="flex items-center gap-1.5 overflow-hidden">
            <RiUser3Line className="size-3.5 shrink-0 text-foreground-muted" />
            <span className="truncate">{displayLabel}</span>
          </span>
          <RiExpandUpDownLine className="size-3.5 shrink-0 text-foreground-muted opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[240px] space-y-1.5 p-1"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <Input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search providers..."
          icon={RiSearchLine}
          className="h-8 rounded-lg text-caption"
        />
        <div className="max-h-48 space-y-1 overflow-y-auto">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start rounded-md text-foreground-muted italic"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
          >
            Unassigned
          </Button>
          {filtered.map((p) => (
            <Button
              key={p.id}
              variant="ghost"
              size="sm"
              className="w-full justify-start rounded-md"
              onClick={() => {
                onChange(p.id);
                setOpen(false);
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <span className="flex-1 text-left truncate">
                  {formatStaffName(p)}
                </span>
                {value === p.id && (
                  <RiCheckLine className="size-3.5 text-foreground" />
                )}
              </div>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- Team Search Select ---
export function TeamBadge({
  value,
  onChange,
  className,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: teams = [] } = useTeams();

  const filtered = teams.filter((t: Team) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedTeam = teams.find((t: Team) => t.id === value);
  const displayLabel = selectedTeam ? selectedTeam.name : "No team";

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cx(
            subtleTriggerClasses,
            "!justify-between group",
            className,
          )}
        >
          <span className="flex items-center gap-1.5 overflow-hidden">
            <RiUser3Line className="size-3.5 shrink-0 text-foreground-muted" />
            <span className="truncate">{displayLabel}</span>
          </span>
          <RiExpandUpDownLine className="size-3.5 shrink-0 text-foreground-muted opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[240px] space-y-1.5 p-1"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <Input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search teams..."
          icon={RiSearchLine}
          className="h-8 rounded-lg text-caption"
        />
        <div className="max-h-48 space-y-1 overflow-y-auto">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start rounded-md text-foreground-muted italic"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
          >
            No team
          </Button>
          {filtered.map((t: Team) => (
            <Button
              key={t.id}
              variant="ghost"
              size="sm"
              className="w-full justify-start rounded-md"
              onClick={() => {
                onChange(t.id);
                setOpen(false);
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <span className="flex-1 text-left truncate">
                  {t.name}
                  <span className="ml-1 text-foreground-muted">
                    ({t.members.length})
                  </span>
                </span>
                {value === t.id && (
                  <RiCheckLine className="size-3.5 text-foreground" />
                )}
              </div>
            </Button>
          ))}
          {filtered.length === 0 && search && (
            <div className="px-2.5 py-2 text-caption text-foreground-muted">
              No teams found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- Location Search Select ---
export function LocationBadge({
  value,
  onChange,
  className,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const { locations, allLocationNameById } = useLocations();

  const filtered = locations.filter((l) =>
    l.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedLocation = locations.find((l) => l.id === value);
  // Distinguish "no location" from a location the current user can't see (not
  // in their visible set) so the trigger never mislabels a set location as none.
  const displayLabel = !value
    ? "No location"
    : (selectedLocation?.name ?? allLocationNameById[value] ?? "Location");

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cx(
            subtleTriggerClasses,
            "!justify-between group",
            className,
          )}
        >
          <span className="flex items-center gap-1.5 overflow-hidden">
            <RiMapPinLine className="size-3.5 shrink-0 text-foreground-muted" />
            <span className="truncate">{displayLabel}</span>
          </span>
          <RiExpandUpDownLine className="size-3.5 shrink-0 text-foreground-muted opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[240px] space-y-1.5 p-1"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <Input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search locations..."
          icon={RiSearchLine}
          className="h-8 rounded-lg text-caption"
        />
        <div className="max-h-48 space-y-1 overflow-y-auto">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start rounded-md text-foreground-muted italic"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
          >
            No location
          </Button>
          {filtered.map((l) => (
            <Button
              key={l.id}
              variant="ghost"
              size="sm"
              className="w-full justify-start rounded-md"
              onClick={() => {
                onChange(l.id);
                setOpen(false);
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <span className="flex-1 text-left truncate">{l.name}</span>
                {value === l.id && (
                  <RiCheckLine className="size-3.5 text-foreground" />
                )}
              </div>
            </Button>
          ))}
          {filtered.length === 0 && search && (
            <div className="px-2.5 py-2 text-caption text-foreground-muted">
              No locations found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- Patient Search Select ---
/** The only patient fields the picker displays. Callers that hold a partial
 *  patient — a task's PatientTiny, an AI suggestion — can pass it as-is instead
 *  of casting to a full PatientSummary they don't have. Names are optional
 *  because those sources have them optional; formatName covers the gaps. */
export interface PickerPatient {
  id: string;
  firstName?: string;
  lastName?: string;
}

export function PatientBadge({
  value,
  onChange,
  className,
}: {
  value: PickerPatient | undefined;
  // Selections come from patient search, which returns full summaries.
  onChange: (v: PatientSummary | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: results = [], isLoading } = useQuery({
    ...patientQueryOptions.search(search),
    enabled: search.length >= 2,
  });

  const displayLabel = value
    ? formatName(value.firstName, value.lastName)
    : "No patient";

  const pickerContent = (
    <PopoverContent
      align="start"
      className="w-[260px] p-1"
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        setTimeout(() => searchRef.current?.focus(), 0);
      }}
    >
      <div className="px-1 pb-1">
        <Input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patients..."
          icon={RiSearchLine}
          className="h-8 text-caption"
        />
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start rounded-md text-foreground-muted italic"
          onClick={() => {
            onChange(undefined);
            setOpen(false);
          }}
        >
          No patient
        </Button>
        {search.length < 2 && search !== "" && (
          <div className="px-2.5 py-2 text-caption text-foreground-muted">
            Type at least 2 characters to search
          </div>
        )}
        {isLoading && search.length >= 2 && (
          <div className="px-2.5 py-2 text-caption text-foreground-muted flex items-center gap-2">
            <div className="size-3 animate-spin rounded-full border border-current border-t-transparent" />
            Searching...
          </div>
        )}
        {results.map((p) => (
          <Button
            key={p.id}
            variant="ghost"
            size="sm"
            className="w-full justify-start rounded-md"
            onClick={() => {
              onChange(p);
              setOpen(false);
            }}
          >
            <div className="flex items-center gap-2 w-full">
              <span className="flex-1 text-left truncate">
                {p.firstName} {p.lastName}
              </span>
              {value?.id === p.id && (
                <RiCheckLine className="size-3.5 text-foreground" />
              )}
            </div>
          </Button>
        ))}
        {search.length >= 2 && !isLoading && results.length === 0 && (
          <div className="px-2.5 py-2 text-caption text-foreground-muted">
            No patients found
          </div>
        )}
      </div>
    </PopoverContent>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      {value ? (
        <div className={cx("flex min-w-0 items-center pl-3", className)}>
          <RiUser3Line className="size-3.5 shrink-0 text-foreground-muted" />
          <PatientDisplay
            patient={{
              id: value.id,
              firstName: value.firstName ?? "",
              lastName: value.lastName,
              type: UserType.PATIENT,
            }}
            showMemberships={false}
            className="min-w-0 pl-1.5"
            nameClassName="md:text-caption-emphasis"
          />
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cx(subtleTriggerClasses, "px-1")}
              aria-label="Change patient"
            >
              <RiExpandUpDownLine className="size-3.5 shrink-0 text-foreground-muted opacity-50" />
            </Button>
          </PopoverTrigger>
        </div>
      ) : (
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cx(
              subtleTriggerClasses,
              "!justify-between group",
              className,
            )}
          >
            <span className="flex items-center gap-1.5 overflow-hidden">
              <RiUserHeartLine className="size-3.5 shrink-0 text-foreground-muted" />
              <span className="truncate">{displayLabel}</span>
            </span>
            <RiExpandUpDownLine className="size-3.5 shrink-0 text-foreground-muted opacity-50" />
          </Button>
        </PopoverTrigger>
      )}
      {pickerContent}
    </Popover>
  );
}

// --- Date Picker Badge ---
export function DateBadge({
  label,
  value,
  onChange,
  className,
}: {
  label: string;
  value: Date | undefined;
  onChange: (v: Date | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // `value` is a picked day, so it is labelled as that day.
  const displayLabel = value ? formatDate(pickedDay(value)) : label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cx(
            subtleTriggerClasses,
            "!justify-between group",
            className,
          )}
        >
          <span className="flex items-center gap-1.5 overflow-hidden">
            <RiCalendarLine className="size-3.5 shrink-0 text-foreground-muted" />
            <span className="truncate">{displayLabel}</span>
          </span>
          <RiExpandUpDownLine
            className={cx(
              "size-3.5 shrink-0 text-foreground-muted transition-transform group-data-[state=open]:rotate-180 opacity-50",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-fit p-0">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
          defaultMonth={value ?? new Date()}
        />
        {value && (
          <div className="border-t border-border px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-foreground-muted"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// --- Subscribers Badge + Popover ---
export function SubscribersBadge({
  subscriberIds,
  assigneeId,
  onChange,
  className,
}: {
  subscriberIds: string[];
  assigneeId?: string;
  onChange: (providerIds: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const { providers } = useProviders({ hasFirebaseUid: true });

  // Only count subscribers that exist in the providers list
  const visibleSubscriberIds = useMemo(() => {
    const providerIdSet = new Set(providers.map((p) => p.id));
    return subscriberIds.filter((id) => providerIdSet.has(id));
  }, [subscriberIds, providers]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    const all = providers.filter((p) =>
      formatStaffName(p).toLowerCase().includes(term),
    );
    // Subscribed providers first
    const subscribed = all.filter((p) => subscriberIds.includes(p.id));
    const unsubscribed = all.filter((p) => !subscriberIds.includes(p.id));
    return [...subscribed, ...unsubscribed];
  }, [providers, search, subscriberIds]);

  const toggleProvider = (providerId: string) => {
    const next = subscriberIds.includes(providerId)
      ? subscriberIds.filter((id) => id !== providerId)
      : [...subscriberIds, providerId];
    onChange(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cx(
            "flex items-center gap-1 rounded-md px-2 py-1 -ml-2 h-7 cursor-pointer hover:bg-hover transition-colors",
            className,
          )}
        >
          {visibleSubscriberIds.length > 0 ? (
            <div className="flex items-center -space-x-1.5">
              {visibleSubscriberIds.slice(0, 3).map((id) => (
                <ProviderAvatar
                  key={id}
                  providerId={id}
                  size={20}
                  fontSize={8}
                  className="ring-2 ring-background"
                />
              ))}
              {visibleSubscriberIds.length > 3 && (
                <span className="pl-2 text-caption text-foreground-muted">
                  +{visibleSubscriberIds.length - 3}
                </span>
              )}
            </div>
          ) : (
            <span className="text-caption text-foreground-muted">Add...</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[260px] p-1 pointer-events-auto"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <div className="px-1 pb-1">
          <Input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search providers..."
            icon={RiSearchLine}
            className="h-8 rounded-lg text-caption"
          />
        </div>
        <div className="max-h-48 space-y-0.5 overflow-y-auto">
          {filtered.map((p) => {
            const isChecked = subscriberIds.includes(p.id);
            const isAssignee = p.id === assigneeId;
            return (
              <Label
                key={p.id}
                className="flex w-full gap-2 rounded-md px-2 py-1.5 text-body hover:bg-hover transition-colors cursor-pointer"
                onClick={() => toggleProvider(p.id)}
              >
                <div
                  className={cx(
                    "flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                    isChecked
                      ? "border-primary bg-primary text-white"
                      : "border-border",
                  )}
                >
                  {isChecked && <RiCheckLine className="size-3" />}
                </div>
                <span className="flex-1 truncate text-caption">
                  {formatStaffName(p)}
                </span>
                {isAssignee && (
                  <span className="shrink-0 rounded bg-background-secondary px-1.5 py-0.5 text-tiny text-foreground-muted">
                    Assignee
                  </span>
                )}
              </Label>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-2.5 py-2 text-caption text-foreground-muted">
              No providers found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
